import React, { useCallback, useMemo, useRef, useState } from "react";
import { Card, Table, Input, Button, Space, Empty, App, Modal, Tooltip } from "antd";
import {
  DownloadOutlined,
  LeftOutlined,
  SoundOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import { ReactComponent as WarningCircleIcon } from "../../assets/icons/warningCircle.svg";
import { ReactComponent as FailedCircleIcon } from "../../assets/icons/failedCircle.svg";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import theme from "../../themes";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as CampaignIcon } from "../../assets/icons/campaign.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import { getCampaignReportThunk } from "../../store/actions/campaign.action";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { generateDynamicColumns } from "./utils";
import { getCallStatusLabel } from "../../constants/callStatus";
import { getPresignedAudioUrl } from "../../services/campaign.service";
import ToastMessage from "../../utils/toastMessage";

dayjs.extend(utc);

const MAX_INBOUND_CALLS = 1000;

const CampaignDetail: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [callTimeOrder, setCallTimeOrder] = useState<"ascend" | "descend">(
    "descend"
  );
  const pageSize = 10;
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [campaignReport, setCampaignReport] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const { campaignName, agentName, isInbound } = (location.state as any) || {};
  const isInboundReport = Boolean(isInbound || agentName);
  const { loading } = useAppSelector((state) => state.campaign);
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const [summaryDetailRecord, setSummaryDetailRecord] = useState<any | null>(
    null
  );

  const getSummaryStatusLabel = (rawStatus: any) => {
    if (!rawStatus) return "";
    const key = String(rawStatus);
    const knownKeys = ["achieved", "partially_achieved", "not_achieved"];
    const lookupKey = knownKeys.includes(key) ? key : "unknown";
    const translated = t(`reports.summaryStatus.${lookupKey}`);
    if (
      typeof translated === "string" &&
      translated !== `reports.summaryStatus.${lookupKey}`
    ) {
      return translated;
    }
    return key;
  };

  const fetchCampaignReport = useCallback(async () => {
    try {
      const res = await dispatch(
        getCampaignReportThunk({
          campaignId: isInboundReport ? "" : (id as string),
          agentId: isInboundReport ? (id as string) : "",
          ...(isInboundReport
            ? { limit: MAX_INBOUND_CALLS, direction: "inbound" }
            : {}),
        })
      ).unwrap();
      const data = res.data.map((item: any, index: number) => ({
        ...item,
        key: item.key || item._id || item.id || `row-${index}`,
        phone: isInboundReport ? item.from : item.to,
        duration:
          item.call_duration !== undefined && item.call_duration !== null
            ? item.call_duration
            : item.duration,
      }));

      setCampaignReport(data);
    } catch (error) {
      console.log(error);
    }
  }, [isInboundReport, campaignName, dispatch, id]);

  const renderGoalIcon = (rawStatus: any) => {
    const key = String(rawStatus || "unknown");
    if (key === "achieved") {
      return <CheckCircleIcon width={20} height={20} />;
    }
    if (key === "partially_achieved") {
      return <WarningCircleIcon width={20} height={20} />;
    }
    if (key === "not_achieved") {
      return <FailedCircleIcon width={20} height={20} />;
    }
    return null;
  };

  const handleDownloadAudio = useCallback(
    async (
      storageUrl: string | null,
      sid: string | null,
      recipientName?: string
    ) => {
      if (!storageUrl && !sid) return;

      try {
        // First, get the presigned URL from the backend API
        const presignedResponse = await getPresignedAudioUrl(
          storageUrl || null,
          sid || null
        );
        const presignedUrl = presignedResponse.data?.data;

        if (!presignedUrl) {
          throw new Error("Failed to get presigned URL");
        }

        // Try to get file extension from URL
        let fileExtension = "wav";
        try {
          const urlObj = new URL(presignedUrl);
          const match = urlObj.pathname.match(/\.([^.]+)$/);
          if (match) {
            fileExtension = match[1];
          }
        } catch {
          // If URL parsing fails, try to extract extension from original URL
          if (storageUrl) {
            const match = storageUrl.match(/\.([^.]+)(?:\?|$)/);
            if (match) {
              fileExtension = match[1];
            }
          }
        }

        // Use direct link download with presigned URL to avoid CORS issues
        const fileName = recipientName
          ? `call_audio_${recipientName.replace(/\s+/g, "_")}.${fileExtension}`
          : `call_audio_${Date.now()}.${fileExtension}`;

        const link = document.createElement("a");
        link.href = presignedUrl;
        link.setAttribute("download", fileName);
        link.setAttribute("target", "_blank");
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error: any) {
        console.error("Error downloading audio:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to download audio file";
        toast.error(errorMessage, 5);
      }
    },
    [toast]
  );

  const handleDownloadAudioRef = useRef(handleDownloadAudio);
  handleDownloadAudioRef.current = handleDownloadAudio;

  const getAudioUrl = (record: any): string | null => {
    if (record.storage_url) {
      return record.storage_url;
    }

    return null;
  };

  const filteredCampaignReport = useMemo(() => {
    const keyword = search.trim();

    let result = campaignReport;
    if (keyword) {
      const normalizePhone = (value: string) => value.replace(/[^\d+]/g, "");
      const normalizedKeyword = normalizePhone(keyword);

      result = campaignReport.filter((item: any) => {
        const phone = String(item?.phone || "");
        const normalizedPhone = normalizePhone(phone);

        if (normalizedKeyword) {
          return normalizedPhone.includes(normalizedKeyword);
        }

        return phone.toLowerCase().includes(keyword.toLowerCase());
      });
    }

    return [...result].sort((a: any, b: any) => {
      const aTime = a?.start_time ? dayjs(a.start_time).valueOf() : 0;
      const bTime = b?.start_time ? dayjs(b.start_time).valueOf() : 0;
      return callTimeOrder === "ascend" ? aTime - bTime : bTime - aTime;
    });
  }, [campaignReport, search, callTimeOrder]);

  React.useEffect(() => {
    if (campaignReport && campaignReport.length > 0) {
      const generatedColumns = generateDynamicColumns(
        campaignReport,
        t,
        (record: any) => {
          setSummaryDetailRecord(record);
        },
        {
          order: callTimeOrder,
          onToggle: () =>
            setCallTimeOrder((prev) =>
              prev === "ascend" ? "descend" : "ascend"
            ),
        }
      );

      // Always add download audio column
      const downloadAudioColumn = {
        title: t("reports.tableColumns.audio") || "Audio",
        key: "download_audio",
        width: 100,
        fixed: "right" as const,
        render: (_: any, record: any) => {
          const audioUrl = getAudioUrl(record);

          return (
            <AudioDownloadButton
              type="link"
              icon={<SoundOutlined />}
              onClick={() => {
                if (audioUrl || record.sid) {
                  handleDownloadAudioRef.current(
                    audioUrl || null,
                    record.sid || null,
                    record.recipient_name
                  );
                }
              }}
              disabled={!audioUrl && !record.sid}
              title={t("reports.downloadAudio") || "Download Audio"}
            >
              {t("reports.download") || "Download"}
            </AudioDownloadButton>
          );
        },
      };
      setColumns([...generatedColumns, downloadAudioColumn]);
    }
    // Only regenerate columns when report data or sort order changes; t/handleDownloadAudio are used via closure
  }, [campaignReport, callTimeOrder]);

  const handleDownloadCSV = () => {
    if (!campaignReport.length || !columns.length) return;

    const tableHeaders = columns.map((col) => {
      if (col.title?.props?.children?.[0]) {
        return col.title.props.children[0];
      }
      if (typeof col.title === "string") {
        return col.title;
      }
      return col.title;
    });
    const hasUnsubscribed = campaignReport.some(
      (row: any) =>
        String(row.call_analysis?.unsubscribe || "")
          .trim()
          .toUpperCase() === "YES"
    );
    const headers = [
      ...tableHeaders,
      ...(hasUnsubscribed ? ["Unsubscribe"] : []),
      t("reports.tableColumns.goalAchievedStatus") || "Goal achieved status",
    ];

    const rows = campaignReport.map((row: any) => {
      const tableRow = columns.map((col) => {
        if (col.dataIndex === "recipient_name")
          return row.recipient_name || "-";
        if (col.dataIndex === "phone") return row.phone || "-";
        if (col.dataIndex === "duration") {
          const duration =
            row.call_duration !== undefined && row.call_duration !== null
              ? row.call_duration
              : row.duration;
          return duration || "-";
        }
        if (col.dataIndex === "status") return getCallStatusLabel(row.status);
        if (col.dataIndex === "start_time") {
          return row.start_time
            ? dayjs(row.start_time).format("hh:mm A, MMM DD, YYYY")
            : "-";
        }
        if (col.key === "summary") {
          const callAnalysis = row.call_analysis;
          const followUp = callAnalysis?.follow_up;
          const summary = callAnalysis?.summary;
          const reason = callAnalysis?.reason;
          const recommendation =
            typeof followUp === "string" ? followUp : followUp?.recommendation;

          if (summary || reason || recommendation) {
            const parts = [];
            if (summary) parts.push(`Summary: ${summary}`);
            if (reason) parts.push(`Reason: ${reason}`);
            if (recommendation) parts.push(`Follow up: ${recommendation}`);
            if (parts.length > 0) return parts.join(" | ");
          }

          return row.summary || "-";
        }

        // Handle dynamic action columns
        if (Array.isArray(col.dataIndex) && col.dataIndex[0] === "action") {
          const actionKey = col.dataIndex[1];
          const actionData = row.action?.[actionKey];

          if (!actionData || actionData.value == null) {
            return "-";
          }

          const items = Array.isArray(actionData.value)
            ? actionData.value
            : [actionData.value];

          if (items.length === 0) return "-";

          const formatTimeSlot = (slot: any) => {
            const { start_time, end_time } = slot || {};
            if (!start_time && !end_time) return "-";
            try {
              const startTime = start_time ? dayjs.utc(start_time) : null;
              const endTime = end_time ? dayjs.utc(end_time) : null;

              if (!startTime && endTime)
                return `${endTime.format("h:mm A")}, ${endTime.format(
                  "MMM DD, YYYY"
                )}`;
              if (startTime && !endTime)
                return `${startTime.format("h:mm A")}, ${startTime.format(
                  "MMM DD, YYYY"
                )}`;

              if (startTime?.isValid() && endTime?.isValid()) {
                if (startTime.isSame(endTime, "day")) {
                  return `${startTime.format("h:mm A")} - ${endTime.format(
                    "h:mm A"
                  )}, ${startTime.format("MMM DD, YYYY")}`;
                } else {
                  return `${startTime.format("h:mm A")}, ${startTime.format(
                    "MMM DD, YYYY"
                  )} - ${endTime.format("h:mm A")}, ${endTime.format(
                    "MMM DD, YYYY"
                  )}`;
                }
              }
              return (
                `${start_time || ""} - ${end_time || ""}`.replace(
                  /^- | -$/,
                  ""
                ) || "-"
              );
            } catch (e) {
              return (
                `${start_time || ""} - ${end_time || ""}`.replace(
                  /^- | -$/,
                  ""
                ) || "-"
              );
            }
          };

          const normalizeValue = (val: any) => {
            if (typeof val === "string") {
              if (/[<>]/.test(val)) {
                return val.replace(/<[^>]*>/g, "").trim() || val;
              }
              return val;
            }
            return JSON.stringify(val);
          };

          const toLabel = (key: string) =>
            key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

          const serializeItem = (item: any) => {
            if (item && typeof item === "object") {
              const hasTime = Boolean(item.start_time || item.end_time);
              const parts: string[] = [];
              if (hasTime) {
                const label =
                  item.start_time && item.end_time
                    ? `${toLabel("start_time")} - ${toLabel("end_time")}`
                    : item.start_time
                    ? toLabel("start_time")
                    : toLabel("end_time");
                parts.push(`${label}: ${formatTimeSlot(item)}`);
              }
              Object.keys(item)
                .filter((k) => k !== "start_time" && k !== "end_time")
                .forEach((k) => {
                  parts.push(`${toLabel(k)}: ${normalizeValue(item[k])}`);
                });
              return parts.join(" | ");
            }
            return normalizeValue(item);
          };

          return items.map(serializeItem).join(";\n");
        }

        return "-";
      });
      const isUnsubscribed =
        String(row.call_analysis?.unsubscribe || "").trim().toUpperCase() ===
        "YES";
      return [
        ...tableRow,
        ...(hasUnsubscribed ? [isUnsubscribed ? "YES" : "-"] : []),
        getSummaryStatusLabel(row.call_analysis?.goal_achieved),
      ];
    });

    const csvContent = [headers, ...rows]
      .map((e) =>
        e
          .map((field) =>
            typeof field === "string" && field.includes(",")
              ? `"${field.replace(/"/g, '""')}"`
              : field
          )
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `${campaignName || agentName || "campaign_report"}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  React.useEffect(() => {
    fetchCampaignReport();
  }, [fetchCampaignReport]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <MainContent>
      <BackLink onClick={() => navigate(-1)}>
        <LeftOutlined style={{ marginRight: 8 }} /> {t("reports.backToReports")}
      </BackLink>
      <HeaderRow>
        <Title>
          <CampaignIcon style={{ marginRight: 8 }} />
          <span>{campaignName || agentName}</span>
        </Title>
        <DownloadButton
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadCSV}
        >
          {t("reports.downloadCsv")}
        </DownloadButton>
      </HeaderRow>
      <CardWrapper>
        <Space
          style={{
            marginBottom: 16,
            width: "100%",
            justifyContent: "flex-end",
            display: "flex",
          }}
        >
          <SearchWrapper>
            <SearchIcon />
            <CustomSearch
              placeholder={t("reports.searchPhoneNumber")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchWrapper>
        </Space>
        <StyledTable
          dataSource={filteredCampaignReport}
          columns={columns}
          loading={loading}
          pagination={{
            pageSize,
            position: ["bottomCenter"],
            current: currentPage,
            onChange: setCurrentPage,
            showSizeChanger: false,
          }}
          rowKey="key"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <EmptyStateTitle>{t("common.noData")}</EmptyStateTitle>
                }
              />
            ),
          }}
          scroll={{ x: true }}
        />
        <StyledSummaryModal
          open={!!summaryDetailRecord}
          onCancel={() => setSummaryDetailRecord(null)}
          footer={null}
          title={
            <ModalTitleRow>
              <ModalTitleIcon>
                <FileTextOutlined />
              </ModalTitleIcon>
              <ModalTitleText>
                {t("reports.summaryDetailTitle") ||
                  t("reports.tableColumns.summary") ||
                  "Call details"}
              </ModalTitleText>
              {summaryDetailRecord?.recipient_name && (
                <RecipientBadge>
                  {summaryDetailRecord.recipient_name}
                </RecipientBadge>
              )}
            </ModalTitleRow>
          }
          width={660}
        >
          {summaryDetailRecord && (
            <SummaryModalBody>
              {(() => {
                const ca = summaryDetailRecord.call_analysis || {};
                const followUp = ca.follow_up ?? summaryDetailRecord.follow_up;
                const rawStatus = ca.goal_achieved;
                const status = getSummaryStatusLabel(rawStatus);
                const goalIcon = renderGoalIcon(rawStatus);
                const goalText = followUp?.goal ?? ca.goal ?? status;
                const summary =
                  ca.summary ??
                  ca.root_cause_analysis?.summary ??
                  summaryDetailRecord.summary;
                const reason = ca.reason ?? followUp?.reason;
                const recommendation =
                  typeof followUp === "string"
                    ? followUp
                    : followUp?.recommendation;
                const isUnsubscribed =
                  String(ca.unsubscribe || "").trim().toUpperCase() === "YES";

                return (
                  <>
                    {goalText && (
                      <SummarySection>
                        <SectionLabel>
                          {t("reports.summaryStatus.agentGoal") || "Agent goal"}
                        </SectionLabel>
                        <AgentGoalRow>
                          <span>{goalText}</span>
                          {goalIcon && (
                            <Tooltip title={status || undefined}>
                              <IconWrap aria-label={status || undefined}>
                                {goalIcon}
                              </IconWrap>
                            </Tooltip>
                          )}
                        </AgentGoalRow>
                      </SummarySection>
                    )}
                    {summary && (
                      <SummarySection>
                        <SectionLabel>
                          {t("reports.tableColumns.summary") || "Summary"}
                        </SectionLabel>
                        <SectionContent>{summary}</SectionContent>
                      </SummarySection>
                    )}
                    {reason && (
                      <SummarySection>
                        <SectionLabel>
                          {t("reports.reason") || "Reason"}
                        </SectionLabel>
                        <SectionContent>{reason}</SectionContent>
                      </SummarySection>
                    )}
                    {recommendation && (
                      <SummarySection $highlight>
                        <SectionLabel>
                          {t("reports.followUp") || "Follow up"}
                        </SectionLabel>
                        <FollowUpContent>{recommendation}</FollowUpContent>
                      </SummarySection>
                    )}
                    {isUnsubscribed && (
                      <SummarySection>
                        <SectionLabel>Unsubscribe</SectionLabel>
                        <SectionContent>YES</SectionContent>
                      </SummarySection>
                    )}
                  </>
                );
              })()}
            </SummaryModalBody>
          )}
        </StyledSummaryModal>
      </CardWrapper>
    </MainContent>
  );
};

export default CampaignDetail;

// Styled Components
const MainContent = styled.div`
  flex: 1;
  padding: 52px 72px;
  gap: 24px;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.grey3};
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const BackLink = styled.div`
  color: ${theme.colors.blue1};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  height: 40px;
  display: flex;
  align-items: center;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
`;

const SearchWrapper = styled.div`
  width: 240px;
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0px 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  .ant-table-tbody > tr:nth-child(odd) > td.ant-table-cell-fix-right {
    background: ${theme.colors.grey3} !important;
  }

  .ant-table-tbody > tr:nth-child(even) > td.ant-table-cell-fix-right {
    background: ${theme.colors.grey4} !important;
  }

  /* Disable row hover background (AntD uses td background on hover) */
  .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-tbody > tr:hover > td {
    background: inherit !important;
  }

  /* Disable hover for fixed-right cells too */
  .ant-table-tbody > tr.ant-table-row:hover > td.ant-table-cell-fix-right,
  .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
    background: inherit !important;
  }

  /* Fixed header cell background */
  .ant-table-thead > tr > th.ant-table-cell-fix-right {
    background: #fff !important;
  }

  /* Apply border radius to fixed column cells */
  .ant-table-tbody > tr > td.ant-table-cell-fix-right {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`;

const DownloadButton = styled(Button)`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #047857 !important;
  }
`;

const AudioDownloadButton = styled(Button)`
  padding: 0;
  height: auto;
  color: ${theme.colors.blue1};
  font-weight: 500;

  &:hover {
    color: ${theme.colors.blue1} !important;
  }
`;

const StyledSummaryModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
  .ant-modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid ${theme.colors.grey2};
  }
  .ant-modal-title {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
  }
  .ant-modal-body {
    padding: 0;
    background: #fff;
  }
  .ant-modal-close {
    top: 20px;
    inset-inline-end: 24px;
    color: ${theme.colors.grey1};
  }
  .ant-modal-close:hover {
    color: #374151;
  }
`;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ModalTitleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${theme.colors.blue2};
  color: ${theme.colors.blue1};
  font-size: 18px;
  flex-shrink: 0;
`;

const ModalTitleText = styled.span`
  flex: 1;
  min-width: 0;
`;

const RecipientBadge = styled.span`
  padding: 4px 12px;
  border-radius: 16px;
  background: ${theme.colors.grey3};
  color: #4b5563;
  font-size: 13px;
  font-weight: 500;
`;

const SummaryModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

type SummarySectionProps = { $highlight?: boolean };
const SummarySection = styled.div<SummarySectionProps>``;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.grey1};
  margin-bottom: 6px;
`;

const SectionContent = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
`;

const FollowUpContent = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #111827;
  font-weight: 500;
`;

const AgentGoalRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #111827;
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;

  svg {
    display: block;
  }
`;
